/**
 * Analytics Utilities
 * UTM capture, device detection, and common helpers
 */

const UTM_STORAGE_KEY = 'visualizer_utm';
const SESSION_ID_KEY = 'visualizer_session_id';

/**
 * Capture UTM parameters from URL and store in sessionStorage
 * @returns {Object} UTM data object
 */
export function captureUTM() {
  const params = new URLSearchParams(window.location.search);
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  const utmData = {};

  utmKeys.forEach(key => {
    if (params.has(key)) {
      utmData[key] = params.get(key);
    }
  });

  if (Object.keys(utmData).length > 0) {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
    console.log('[Analytics] Captured UTM:', utmData);
  }

  return utmData;
}

/**
 * Get stored UTM data
 * @returns {Object} UTM data object
 */
export function getUTMData() {
  try {
    return JSON.parse(sessionStorage.getItem(UTM_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Detect device type from user agent and screen size
 * @returns {'mobile'|'tablet'|'desktop'}
 */
export function getDeviceType() {
  const ua = navigator.userAgent.toLowerCase();
  const width = window.innerWidth;

  // Check for mobile user agents
  const isMobileUA = /mobile|android|iphone|ipod|blackberry|opera mini|iemobile/i.test(ua);
  const isTabletUA = /tablet|ipad/i.test(ua);

  if (isTabletUA || (width >= 768 && width <= 1024 && !isMobileUA)) {
    return 'tablet';
  }
  if (isMobileUA || width < 768) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Get connection type if available
 * @returns {string|null}
 */
export function getConnectionType() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return connection?.effectiveType || null;
}

/**
 * Get or create a session ID for the current browser session
 * @returns {string}
 */
export function getSessionId() {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

/**
 * Get device information for session tracking
 * @returns {Object}
 */
export function getDeviceInfo() {
  return {
    device_type: getDeviceType(),
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    user_agent: navigator.userAgent,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    language: navigator.language || null,
    connection_type: getConnectionType()
  };
}

/**
 * Get referrer, cleaned for analytics
 * @returns {string|null}
 */
export function getReferrer() {
  const referrer = document.referrer;
  if (!referrer) return null;

  try {
    // Don't track same-origin referrers
    const referrerUrl = new URL(referrer);
    if (referrerUrl.origin === window.location.origin) {
      return null;
    }
    return referrer;
  } catch {
    return referrer;
  }
}

/**
 * Build common context data for analytics events
 * @param {Object} options
 * @param {string} options.posthogDistinctId - PostHog distinct ID
 * @param {string} options.gaClientId - GA client ID
 * @returns {Object}
 */
export function getCommonContext({ posthogDistinctId = null, gaClientId = null } = {}) {
  const utmData = getUTMData();

  return {
    posthog_distinct_id: posthogDistinctId,
    ga_client_id: gaClientId,

    // Attribution
    utm_source: utmData.utm_source || null,
    utm_medium: utmData.utm_medium || null,
    utm_campaign: utmData.utm_campaign || null,
    utm_term: utmData.utm_term || null,
    utm_content: utmData.utm_content || null,
    referrer: getReferrer()
  };
}

/**
 * Poll for a value to become non-null/undefined.
 * Replaces hard-coded setTimeout delays with adaptive polling.
 * @param {Function} getter - Sync or async function that returns the desired value (or null/undefined if not ready)
 * @param {Object} [options]
 * @param {number} [options.maxAttempts=10] - Maximum number of polling attempts
 * @param {number} [options.intervalMs=200] - Milliseconds between attempts
 * @returns {Promise<*>} The first non-null/undefined value, or null if all attempts exhausted
 */
export async function waitForValue(getter, { maxAttempts = 10, intervalMs = 200 } = {}) {
  for (let i = 0; i < maxAttempts; i++) {
    const value = await getter();
    if (value != null) return value;
    await new Promise(r => setTimeout(r, intervalMs));
  }
  return null;
}

/**
 * Check if we're in a development environment
 * @returns {boolean}
 */
export function isDevelopment() {
  return import.meta.env?.DEV ||
         window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1';
}
